import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {Fullscreen} from '../../previews/player/fullscreen';
import {fairPlaySupported} from '../../previews/player/fairplay-probe';

import {ActivatedRoute} from '@angular/router';
import {BannerService} from '../../../banner/banner.service';
import {HttpClient} from '@angular/common/http';
import {forkJoin, from, of} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {FileTrack} from './folder-asset-view.component';
import {clpp} from '@castlabs/prestoplay'
import '@castlabs/prestoplay/cl.mse'
import '@castlabs/prestoplay/cl.dash'
import '@castlabs/prestoplay/cl.htmlcue'
import '@castlabs/prestoplay/cl.vtt'
import '@castlabs/prestoplay/cl.ttml'
import { environment } from '../../../../environments/environment';


@Component({
  selector: 'app-mezz-preview-player',
  templateUrl: './mezz-preview-player.component.html',
  styleUrls: ['./mezz-preview-player.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: []
})
export class MezzPreviewPlayerComponent implements OnInit, OnDestroy, OnChanges {

  @Input() manifest: string;
  @Input() drmtodayToken: string;
  @Input() accessToken: string;
  @Input() abToken: string;
  @Input() selectedVideoFileTrack!: FileTrack;
  @Input() selectedAudioFileTrack!: FileTrack;
  @Input() selectedTextFileTrack!: FileTrack;

  trackSources: {
    sourceTrackId: number,
    representationId: string,
    sourceFilename: string,
  }[] = []

  player: any;
  error: any;
  airline: string;
  playerState = {
    mouseMoved: false,
    isPlaying: false,
    isMouseInFooter: false,
    progress: 0,
    duration: 0,
    isSeeking: false,
    isSelectingTrack: false,
    isFullscreen: false,
    isLoading: true,
    isInitializing: true
  }
  fullscreenManager: Fullscreen;

  videoRenditions: any[] = [];
  audioRenditions: any[] = [];
  textRenditions: any[] = [];

  progressBarContainer: HTMLElement;

  token: any;

  streamType = ''

  mouseMoveTimeout = null;

  constructor(
    private route: ActivatedRoute,
    private banner: BannerService,
    private cdRef: ChangeDetectorRef,
    private httpClient: HttpClient,
  ) {
    this.airline = this.route.snapshot.params['airline'];
  }

  mouseEnteredControlFooter() {
    this.playerState.isMouseInFooter = true;
  }

  mouseLeftControlFooter() {
    this.playerState.isMouseInFooter = false;
  }


  ngOnChanges(changes: SimpleChanges): void {
    // The first ngOnChanges runs before ngOnInit creates the player. Nothing is lost by waiting: LOADEDMETADATA
    // applies all three current selections -- "no text track" included -- once the stream is loaded.
    if (!this.player) {
      return;
    }
    if (changes['selectedVideoFileTrack'] && this.selectedVideoFileTrack) {
      this.setVideoRendition(this.selectedVideoFileTrack)
    }
    if (changes['selectedAudioFileTrack'] && this.selectedAudioFileTrack) {
      this.setAudioRendition(this.selectedAudioFileTrack)
    }
    // Only when the text selection itself changed: no track selected means subtitles off. (This used to be the
    // else of the line above, so choosing a video or audio track switched subtitles off too.)
    if (changes['selectedTextFileTrack']) {
      this.setTextRendition(this.selectedTextFileTrack)
    }
  }

  startMouseMovedTimer = () => {
    this.playerState.mouseMoved = true;
    this.cdRef.detectChanges();
    clearTimeout(this.mouseMoveTimeout);
    this.mouseMoveTimeout = setTimeout(() => {
      this.playerState.mouseMoved = false;
      this.cdRef.detectChanges()
    }, 2000);
  }

  initFullscreen = () => {
    const playerContainer = document.getElementById('player-container');
    this.fullscreenManager = new Fullscreen(
      playerContainer,
      document,
      () => {
        this.playerState.isFullscreen = this.fullscreenManager.fullscreenEnabled();
      },
    );
  }


  loadPlayer() {

    const fpObs = fairPlaySupported()

    const prObs = from(navigator.requestMediaKeySystemAccess('com.microsoft.playready', [{
      'initDataTypes': ['cenc'],
      'audioCapabilities': [{
        'contentType': 'audio/mp4;codecs="mp4a.40.2"'
      }],
      'videoCapabilities': [{
        'contentType': 'video/mp4;codecs="avc1.42E01E"'
      }]
    }])).pipe(map(() => true), catchError(() => of(false)))

    const wvObs = from(navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
      'initDataTypes': ['cenc'],
      'audioCapabilities': [{
        'contentType': 'audio/mp4;codecs="mp4a.40.2"'
      }],
      'videoCapabilities': [{
        'contentType': 'video/mp4;codecs="avc1.42E01E"'
      }]
    }])).pipe(map(() => true), catchError(() => of(false)))

    const prRobustnessObs = from(navigator.requestMediaKeySystemAccess('com.microsoft.playready', [{
      'initDataTypes': ['cenc'],
      'audioCapabilities': [{
        'robustness': '2000',
        'contentType': 'audio/mp4;codecs="mp4a.40.2"'
      }],
      'videoCapabilities': [{
        'robustness': '2000',
        'contentType': 'video/mp4;codecs="avc1.42E01E"'
      }]
    }])).pipe(map(() => true), catchError(() => of(false)))

    const wvRobustnessObs = from(navigator.requestMediaKeySystemAccess('com.widevine.alpha', [{
      'initDataTypes': ['cenc'],
      'audioCapabilities': [{
        'robustness': 'SW_SECURE_CRYPTO',
        'contentType': 'audio/mp4;codecs="mp4a.40.2"'
      }],
      'videoCapabilities': [{
        'robustness': 'SW_SECURE_CRYPTO',
        'contentType': 'video/mp4;codecs="avc1.42E01E"'
      }]
    }])).pipe(map(() => true), catchError(() => of(false)))
    const obs = forkJoin([prObs, wvObs, prRobustnessObs, wvRobustnessObs, fpObs]).pipe(shareReplay(1))
    obs.subscribe(
      v => {
        if (!(v[2] || v[3] || v[4])) {
          this.banner.open('This browser neither supports PlayReady SL 2000, Widevine SW_SECURE_CRYPTO nor Apple FairPlay . Previews are disabled.', ['Close'])
        }
        if (this.abToken) {
          const networkEngine = this.player.getNetworkEngine();
          const that = this;
          networkEngine.addRequestModifier(function (request) {
            if (request.type === clpp.net.RequestType.MANIFEST) {
              request.headers['token'] = that.abToken;
            } else if (request.type === clpp.net.RequestType.SEGMENT) {
              request.headers['token'] = that.abToken;
            }
          });
        }
        let manifest = '';
        let manifestType = null;
        let preferredDrmSystem = null;

        // Mezzanine previews are packaged DASH-only (shaka:package_dash), so there is
        // one path. The player handles cbcs DASH on every browser we support, Safari
        // included, which is why a FairPlay-only browser is served DASH rather than
        // being refused — it is only the licence system that differs.
        if (v[2] || v[3] || v[4]) {
          manifest = this.manifest
          preferredDrmSystem = (v[2] || v[3]) ? clpp.drm.KeySystem.WIDEVINE : clpp.drm.KeySystem.FAIRPLAY
          this.player.use(clpp.dash.DashComponent);
          manifestType = clpp.Type.DASH
        } else {
          this.banner.open('This browser supports none of PlayReady, Widevine or FairPlay. Previews are disabled.', ['Close'])
        }

        console.log(manifest)
        const playerInit = {
          source: {
            url: manifest,
            type: manifestType
          }
        }
        if (this.drmtodayToken) {
          const payload = JSON.parse(atob(this.drmtodayToken.split('.')[1]))
          const optData = JSON.parse(payload['optData'])
          playerInit['drm'] = {
            env: 'DRMtoday',
            preferredDrmSystem: preferredDrmSystem,
            emeApi: clpp.drm.eme.EmeFactory.Apis.STANDARD,
            customData: {
              userId: optData['userId'],
              sessionId: optData['sessionId'],
              merchant: optData['merchant'],
              authToken: this.drmtodayToken
            }
          }
        }
        this.player.load(playerInit);
      })
    return obs

  }

  ngOnInit() {
    console.log('sdgdf')
    this.httpClient.get<any[]>(this.manifest.replace('Manifest.mpd', 'metadata.json')).subscribe(metadata => {
      for (const key in metadata) {
        if (metadata.hasOwnProperty(key)) {
          this.trackSources.push({
            representationId: metadata[key]['representation id'],
            sourceTrackId: metadata[key]['source track_id'],
            sourceFilename: metadata[key]['source file name'],
          })
        }
      }
    })


    this.player = new clpp.Player(
      'player',
      {
        autoplay: false,
        muted: false,
        license: environment.player_license,
        enableHtmlCue: true,
        textStyle: {
          fontFamily: 'Open Sans',
          fontSize: '2em',
        }
      },
    );
    clpp.install(clpp.htmlcue.HtmlCueComponent);
    clpp.install(clpp.vtt.VttComponent);
    clpp.install(clpp.ttml.TtmlComponent);

    this.player.on('error', err => {
      this.banner.open(err.detail.message, ['Close'])
      console.log(err)
    })

    this.progressBarContainer = document.getElementById('progress-bar-container');
    this.initFullscreen();


    this.player.on('timeupdate', () => {
      if (!this.playerState.isSeeking) {
        this.playerState.progress = this.player.getPosition();
      }
    });
    this.player.on(clpp.events.LOADEDMETADATA, () => {
      this.playerState.duration = this.player.getDuration();
      this.playerState.isLoading = false;
      this.playerState.isInitializing = false;
      this.player.getTextDisplayer().setFontColor('rgba(255, 255, 255, 1)')
      this.cdRef.detectChanges()
      this.startMouseMovedTimer(); // display controls!
      this.setVideoRendition(this.selectedVideoFileTrack);
      this.setAudioRendition(this.selectedAudioFileTrack);
      this.setTextRendition(this.selectedTextFileTrack);
    });
    this.player.on(clpp.events.SEEKING, () => {
      this.playerState.isLoading = true;
    })
    this.player.on(clpp.events.SEEKED, () => {
      this.playerState.isLoading = false;
      this.cdRef.detectChanges()
    })
    this.player.on(clpp.events.ERROR, event => {
      this.error = event.detail;
      // EXPIRED fires once the CDM notices all license keys expired. LICENSE_REQUEST_FAILED with
      // x-dt-resp-code 20002 is DRMToday rejecting a license renewal for the same reason (authtoken
      // expired) before the CDM gets there -- both mean the preview's playback window is up.
      const previewExpired = event.detail.code === clpp.Error.Code.EXPIRED
        || (event.detail.code === clpp.Error.Code.LICENSE_REQUEST_FAILED && event.detail.data?.drmTodayErrorCode === '20002');
      if (previewExpired) {
        this.banner.open('Preview limit of 30m reached', ['Refresh']).subscribe(() => window.location.reload());
      } else if (event.detail.code === clpp.Error.Code.LICENSE_REQUEST_FAILED) {
        // Any other denial under this code -- e.g. WV_VMP_UNMET, LIC_KEYS_NOT_FOUND -- surfaces the
        // x-dt-resp-code so support can look it up without digging into devtools.
        const drmTodayErrorCode = event.detail.data?.drmTodayErrorCode;
        this.banner.open(
          `DRM license request failed${drmTodayErrorCode ? ` (${drmTodayErrorCode})` : ''}`,
          ['Close'],
        );
      }
    });

    this.player.on(clpp.events.TRACKS_ADDED, () => {
      const trackManager = this.player.getTrackManager();
      this.videoRenditions = trackManager.getVideoTracks().map(
        videoTrack => videoTrack.renditions).reduce((prev, curr) => [...prev, ...curr], [])
      this.audioRenditions = trackManager.getAudioTracks().map(
        audioTrack => audioTrack.renditions).reduce((prev, curr) => [...prev, ...curr], [])
      this.textRenditions = trackManager.getTextTracks().map(
        textTrack => textTrack.renditions).reduce((prev, curr) => [...prev, ...curr], [])
    });

    this.loadPlayer()

  }

  ngOnDestroy(): void {
    this.player.destroy();
  }

  play() {
    if (this.playerState.isLoading) {
      return;
    }
    this.player.play().then(
      () => {
        console.log('playing')
        this.playerState.isPlaying = true;
      }
    );
  }

  pause() {
    if (this.playerState.isLoading) {
      return;
    }
    this.player.pause().then(
      () => {
        console.log('paused')
        this.playerState.isPlaying = false;
      }
    );
  }

  isVisible() {
    const {isSeeking, mouseMoved, isSelectingTrack, isInitializing, isMouseInFooter} = this.playerState;
    return (isSeeking || mouseMoved || isSelectingTrack || isMouseInFooter) && !isInitializing;
  }

  getProgressPercent() {
    const duration = Math.max(this.playerState.duration, 0.001);
    return (this.playerState.progress / duration * 100).toFixed(1) + '%';
  }

  getFormattedProgress() {
    const {progress, duration} = this.playerState;
    const padZero = num => num.toString().padStart(2, '0');
    const hours = Math.floor(progress / 3600);
    const minutes = Math.floor((progress - 3600 * hours) / 60);
    const seconds = Math.floor(progress - 3600 * hours - 60 * minutes);
    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
  }

  getFormattedMediaRunTime() {
    const {progress, duration} = this.playerState;
    const padZero = num => num.toString().padStart(2, '0');
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration - 3600 * hours) / 60);
    const seconds = Math.floor(duration - 3600 * hours - 60 * minutes);
    return `${padZero(hours)}:${padZero(minutes)}:${padZero(seconds)}`;
  }


  startSeek(event) {
    const updateProgressOnSeek = (updateProgressOnSeekEvent) => {
      const boundingRect = this.progressBarContainer.getBoundingClientRect();
      this.playerState.progress = this.playerState.duration * Math.max(
        Math.min((updateProgressOnSeekEvent.pageX - boundingRect.left) / boundingRect.width, 1), 0);
    };
    const stopSeek = () => {
      this.playerState.isSeeking = false;
      document.removeEventListener('mousemove', updateProgressOnSeek)
      document.removeEventListener('mouseup', stopSeek);
      this.player.seek(this.playerState.progress);
    };
    document.addEventListener('mouseup', stopSeek);
    document.addEventListener('mousemove', updateProgressOnSeek);
    this.playerState.isSeeking = true;
    updateProgressOnSeek(event);
  }

  setVideoRendition(f: FileTrack) {

    const ts = this.trackSources.find(
      r => r.sourceTrackId === f.track.index &&
        r.sourceFilename === f.file.name)
    if (ts != null) {
      const renditionId = ts['representationId']
      const rendition = this.videoRenditions.find(r => r.originalId === renditionId)
      console.log('rendition', rendition)
      console.log('selectedVideoFileTrack', f)
      console.log('renditionId', renditionId);
      this.player.getTrackManager().setVideoRendition(rendition, true);
    }
  }

  setAudioRendition(f: FileTrack) {
    const ts = this.trackSources.find(
      r => r.sourceTrackId === f.track.index &&
        r.sourceFilename === f.file.name)
    if (ts != null) {
      const renditionId = ts['representationId']
      const rendition = this.audioRenditions.find(r => r.originalId === renditionId)
      console.log('rendition', rendition)
      console.log('selectedAudioFileTrack', f)
      console.log('renditionId', renditionId)
      this.player.getTrackManager().setAudioRendition(rendition, true);
    }
  }

  setTextRendition(f: FileTrack) {
    const normalizeExt = (name: string) =>
      name.replace(/\.[^.]+$/, ext => ext.toLowerCase());
    if (!f) {
      this.player.getTrackManager().setTextRendition(null, true);
    } else {
      const ts = this.trackSources.find(
        r => r.sourceTrackId === f.track.index &&
          r.sourceFilename === normalizeExt(f.file.name))
      if (ts != null) {
        const renditionId = ts['representationId']
        const rendition = this.textRenditions.find(r => r.originalId === renditionId)
        console.log('rendition', rendition)
        console.log('selectedTextFileTrack', f)
        console.log('renditionId', renditionId)
        this.player.getTrackManager().setTextRendition(rendition, true);
      }
    }
  }


}
